import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { translations } from './translations';
import {
  AppBar, Toolbar, Typography, Container, Card,
  CardContent, Box, Button, CssBaseline, ThemeProvider, createTheme,
  Divider, Stack, GlobalStyles, Grid, CircularProgress
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { supabase } from './supabaseClient';
import Login from './Login';
import AdminPage from './AdminPage';
import ProtectedRoute from './ProtectedRoute';

const SUPABASE_URL = "https://rubispuvghopnhtccrcb.supabase.co";
const TRACK_FN = `${SUPABASE_URL}/functions/v1/track-click`;

// --- THEME SETUP ---
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#0066FF' },
    background: { default: '#F8F9FA', paper: '#FFFFFF' },
  },
  typography: { fontFamily: '"Inter", -apple-system, sans-serif' },
});

// --- FADE IN ON SCROLL ---
const FadeInOnScroll = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });
    const currentRef = domRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, []);

  return (
    <Box ref={domRef} sx={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      {children}
    </Box>
  );
};

// --- SIMPLE GRID ---
export const SimpleGrid = ({ partners, lang, customerId }) => {
  const buildTrackUrl = (partner) => {
    if (!partner.web_url) return null;
    const params = new URLSearchParams({
      url: partner.web_url,
      pid: partner.id,
      ...(customerId ? { cid: customerId } : {}),
    });
    return `${TRACK_FN}?${params.toString()}`;
  };

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
      gap: 4
    }}>
      {partners.map((partner) => {
        const name = lang === 'en' ? (partner.name_en || partner.name) : partner.name;
        const description = lang === 'en' ? (partner.description_en || partner.description) : partner.description;
        const trackUrl = buildTrackUrl(partner);

        return (
          <Card key={partner.id} elevation={0} sx={{
            height: '100%', display: 'flex', flexDirection: 'column',
            borderRadius: '24px', bgcolor: '#FFFFFF', border: '1px solid #eef0f2',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 40px rgba(0,0,0,0.04)', borderColor: '#0066FF' }
          }}>
            <CardContent sx={{ p: 4, flexGrow: 1 }}>
              <Box sx={{ height: 60, mb: 3, display: 'flex', alignItems: 'center' }}>
                {partner.logo_url && (
                  <img src={partner.logo_url} alt={name}
                    style={{ maxHeight: '100%', maxWidth: '160px', objectFit: 'contain' }} />
                )}
              </Box>

              <Typography variant="h5" sx={{ mb: 2, fontWeight: 800, color: '#000', letterSpacing: '-0.5px' }}>
                {name}
              </Typography>

              <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, fontSize: '0.95rem', mb: 3 }}>
                {description}
              </Typography>

              <Divider sx={{ mb: 3, opacity: 0.6 }} />

            <Stack spacing={1.5}>
              {partner.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PhoneIcon sx={{ fontSize: '1.1rem', color: '#0066FF' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>
                    🇨🇿 {partner.phone}
                  </Typography>
                </Box>
              )}
              {partner.phone_it && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PhoneIcon sx={{ fontSize: '1.1rem', color: '#0066FF' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#444' }}>
                    🇮🇹 {partner.phone_it}
                  </Typography>
                </Box>
              )}
              {partner.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EmailIcon sx={{ fontSize: '1.1rem', color: '#0066FF' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#444', wordBreak: 'break-all' }}>
                    {partner.email}
                  </Typography>
                </Box>
              )}
            </Stack>
            </CardContent>

            <Box sx={{ px: 4, pb: 4 }}>
              {trackUrl && (
                <Button
                  href={trackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<LanguageIcon />}
                  fullWidth
                  variant="outlined"
                  sx={{
                    fontWeight: 700, textTransform: 'none', borderRadius: '12px',
                    borderColor: '#eef0f2', color: '#000',
                    '&:hover': { bgcolor: '#f8f9fa', borderColor: '#0066FF', color: '#0066FF' }
                  }}
                >
                  {lang === 'en' ? 'Visit website' : 'Navštívit web'}
                </Button>
              )}
            </Box>
          </Card>
        );
      })}
    </Box>
  );
};

// --- MAIN WEBSITE PAGE ---
function WebPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Language Logic
  const isEn = location.pathname.startsWith('/en');
  const lang = isEn ? 'en' : 'cs';
  const t = translations[lang];

  // Read customer ID from URL e.g. ?cid=UUID
  const customerId = new URLSearchParams(location.search).get('cid');

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const { data, error } = await supabase
          .from('partners').select('*').order('order_index', { ascending: true });
        if (error) throw error;
        setPartners(data || []);
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, []);

  const toggleLanguage = () => navigate(isEn ? '/' : '/en');
  const scrollToPartners = () => document.getElementById('partneri')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{ '*': { scrollBehavior: 'smooth' } }} />

{/* NAVIGATION */}
<AppBar position="sticky" elevation={0} sx={{
  bgcolor: 'rgba(255,255,255,0.9)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid #f0f0f0',
  zIndex: 1100
}}>
  <Container maxWidth="lg">
    <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: 80 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        height: '80px', // Matches the Toolbar height
        position: 'relative'
      }}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <img
        src="/simple-blue-logo.png"
        alt="CRONET"
        style={{
          height: '150px', // Slightly adjusted for balance
          width: 'auto',
          display: 'block',
          transform: 'translateY(5px)', // Fine-tune vertical centering
          filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.05))' // Softens the white-on-white edge
        }}
      />
    </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <Typography
          onClick={() => document.getElementById('o-nas')?.scrollIntoView({ behavior: 'smooth' })}
          sx={{
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: 600,
            color: '#1a1a1a',
            '&:hover': { color: '#0066FF' },
            display: { xs: 'none', sm: 'block' }
          }}
        >
          {t.aboutNav}
        </Typography>

        <Button
          onClick={toggleLanguage}
          sx={{ fontWeight: 700, color: '#666', minWidth: 'auto' }}
        >
          {isEn ? 'CZ' : 'EN'}
        </Button>

        <Button
          variant="contained"
          disableElevation
          sx={{
            borderRadius: '100px', // Pill shape looks more premium
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            bgcolor: '#0066FF',
          }}
          onClick={scrollToPartners}
        >
          {t.partnersBtn}
        </Button>
      </Box>
    </Toolbar>
  </Container>
</AppBar>

      {/* HERO */}
      <Box sx={{ pt: { xs: 25, md: 45 }, pb: { xs: 20, md: 40 }, bgcolor: '#FFFFFF', textAlign: 'center', minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="md">
          <FadeInOnScroll>
            <Typography variant="h1" sx={{ mb: 2, fontWeight: 900, fontSize: { xs: '3.5rem', md: '5.5rem' }, lineHeight: 1, letterSpacing: -4, color: '#000' }}>
              {t.heroTitle}
            </Typography>
            <Typography variant="h4" sx={{ mb: 8, fontWeight: 500, color: '#666', fontSize: { xs: '1.2rem', md: '1.8rem' }, letterSpacing: -0.5 }}>
              {t.heroSub}
            </Typography>
            <Button variant="contained" size="large" onClick={scrollToPartners} sx={{ py: 2.5, px: 10, borderRadius: '18px', fontWeight: 700, fontSize: '1.2rem', textTransform: 'none' }}>
              {t.partnersBtn}
            </Button>
          </FadeInOnScroll>
        </Container>
      </Box>

      {/* PARTNERS SECTION */}
      <Box sx={{ pt: { xs: 12, md: 20 }, pb: 15, bgcolor: '#F8F9FA' }} id="partneri">
        <Container maxWidth="lg">
          <FadeInOnScroll>
            <Box sx={{ textAlign: 'left', mb: 10 }}>
              <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: -2, mb: 3, color: '#000', fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                {t.partnersTitle}
              </Typography>
              <Box sx={{ width: '60px', height: '4px', bgcolor: '#0066FF' }} />
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 10 }}><CircularProgress /></Box>
            ) : partners.length === 0 ? (
              <Typography sx={{ color: '#666', py: 10 }}>{t.noPartners}</Typography>
            ) : (
              <SimpleGrid partners={partners} lang={lang} customerId={customerId} />
            )}
          </FadeInOnScroll>
        </Container>
      </Box>

      {/* O NÁS SECTION */}
      <Box id="o-nas" sx={{ py: { xs: 12, md: 20 }, bgcolor: '#FFFFFF', borderTop: '1px solid #eef0f2' }}>
        <Container maxWidth="lg">
          <FadeInOnScroll>
            <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: -2, mb: { xs: 8, md: 12 }, color: '#000', fontSize: { xs: '2.5rem', md: '3.8rem' }, lineHeight: 1 }}>
              {t.aboutTitle}
            </Typography>
          </FadeInOnScroll>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 8, md: 12 }, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <FadeInOnScroll>
                <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.8, mb: 5, fontSize: '1.05rem', maxWidth: '520px' }}>
                  {t.aboutP1}
                </Typography>
                <Box sx={{ pl: 3, borderLeft: '3px solid #0066FF' }}>
                  <Typography variant="body1" sx={{ color: '#000', fontWeight: 700, lineHeight: 1.6, fontSize: '1.1rem' }}>
                    {t.aboutQuote}
                  </Typography>
                </Box>
              </FadeInOnScroll>
            </Box>
            <Box sx={{ flex: 1, width: '100%' }}>
              <FadeInOnScroll>
                <Typography variant="overline" sx={{ color: '#0066FF', fontWeight: 800, letterSpacing: 2, display: 'block', mb: 3 }}>
                  {t.activitiesLabel}
                </Typography>
                <Stack spacing={0}>
                  {t.activitiesList.map((text, index) => (
                    <Box key={index} sx={{ py: 2.2, borderBottom: '1px solid #f2f4f6', display: 'flex', alignItems: 'center', '&:first-of-type': { borderTop: '1px solid #f2f4f6' } }}>
                      <Box sx={{ width: 5, height: 5, bgcolor: '#0066FF', mr: 2.5, flexShrink: 0 }} />
                      <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '0.95rem' }}>{text}</Typography>
                    </Box>
                  ))}
                </Stack>
              </FadeInOnScroll>
            </Box>
          </Box>
        </Container>
      </Box>

{/* EXCLUSIVE DISTRIBUTOR SECTION */}
<Box sx={{ py: { xs: 10, md: 16 }, bgcolor: '#FFFFFF', borderTop: '1px solid #eef0f2' }}>
  <Container maxWidth="lg">
    <FadeInOnScroll>
      <Typography variant="overline" sx={{ color: '#0066FF', fontWeight: 800, letterSpacing: 2, display: 'block', mb: 5 }}>
        {lang === 'en' ? 'Exclusive Distributor' : 'Výhradní distributor'}
      </Typography>

      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 3, sm: 5 },
        bgcolor: '#F8F9FA',
        border: '1px solid #eef0f2',
        borderRadius: '24px',
        p: { xs: 4, md: 5 },
      }}>
        {/* SOMERSET LOGO + WORDMARK */}
        <Box
          component="a"
          href="https://somersetgroup.cz"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            flexShrink: 0,
            pl: { sm: 4 },
            borderLeft: { sm: '1px solid #d8dbde' },
            textDecoration: 'none',
            color: 'inherit',
            transition: 'opacity 0.2s ease',
            '&:hover': { opacity: 0.75 },
          }}
        >
          <Box
            component="img"
            src="/planet-logo-tight.webp"
            alt="Somerset Group"
            sx={{ width: 90, height: 90, objectFit: 'contain', flexShrink: 0 }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography sx={{
              fontWeight: 700,
              fontSize: '1.35rem',
              letterSpacing: '0.2em',
              color: '#0b2347',
              lineHeight: 1,
              pb: 0.8,
              borderBottom: '2px solid #0b2347',
            }}>
              SOMERSET
            </Typography>
            <Typography sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.32em',
              color: '#9aa0a6',
              mt: 0.8,
            }}>
              GROUP
            </Typography>
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, borderColor: '#e4e7ea' }} />

        {/* CONTACT DETAILS */}
        <Box sx={{ flex: 1, width: '100%' }}>
          <Stack spacing={1.6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LanguageIcon sx={{ fontSize: '1.1rem', color: '#0066FF', flexShrink: 0 }} />
              <Typography
                component="a" href="https://somersetgroup.cz" target="_blank" rel="noopener noreferrer"
                sx={{ fontWeight: 500, fontSize: '1rem', color: '#0066FF', textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' } }}
              >
                somersetgroup.cz
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PhoneIcon sx={{ fontSize: '1.1rem', color: '#0066FF', flexShrink: 0 }} />
              <Typography
                component="a" href="tel:+420777330992"
                sx={{ fontWeight: 500, fontSize: '1rem', color: '#555', textDecoration: 'none',
                  '&:hover': { color: '#0066FF' } }}
              >
                +420 777 330 992
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EmailIcon sx={{ fontSize: '1.1rem', color: '#0066FF', flexShrink: 0 }} />
              <Typography
                component="a" href="mailto:info@somersetgroup.cz"
                sx={{ fontWeight: 500, fontSize: '1rem', color: '#555', textDecoration: 'none',
                  '&:hover': { color: '#0066FF' } }}
              >
                info@somersetgroup.cz
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <LocationOnIcon sx={{ fontSize: '1.1rem', color: '#0066FF', flexShrink: 0, mt: '3px' }} />
              <Typography sx={{ fontWeight: 500, fontSize: '1rem', color: '#555', lineHeight: 1.6 }}>
                Somerset Group, s.r.o.<br />
                Vnější 1397/12, 141 00 Praha 4 – Michle
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </FadeInOnScroll>
  </Container>
</Box>

{/* CONTACT PERSON SECTION */}
<Box sx={{ py: { xs: 10, md: 16 }, bgcolor: '#F8F9FA', borderTop: '1px solid #eef0f2' }}>
  <Container maxWidth="lg">
    <FadeInOnScroll>
      <Typography variant="overline" sx={{ color: '#0066FF', fontWeight: 800, letterSpacing: 2, display: 'block', mb: 5 }}>
        {lang === 'en' ? 'Contact' : 'Kontakt'}
      </Typography>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 3,
      }}>
        {[
          {
            initials: 'DS',
            name: 'Daniel Sanvenero',
            title: lang === 'en' ? 'Chairman of the Board' : 'Předseda představenstva',
            web: 'www.cronet.cz',
            webHref: 'https://www.cronet.cz',
            email: 'daniel.sanvenero@cronet.cz',
            phone: '+420 602 149 022',
            phoneHref: 'tel:+420602149022',
          },
          {
            initials: 'MB',
            name: 'Ing. Miroslav Budík',
            title: lang === 'en' ? 'Sales Representative' : 'Obchodní zástupce',
            web: 'www.cronet.cz',
            webHref: 'https://www.cronet.cz',
            email: 'mbudik@cronet.cz',
            phone: '+420 602 565 915',
            phoneHref: 'tel:+420602565915',
          },
            {
              initials: 'IS',
              name: 'Iva Sanvenero',
              title: '\u00A0',
              web: 'www.cronet.cz',
              webHref: 'https://www.cronet.cz',
              email: 'iva.sanvenero@cronet.cz',
              phone: '+420 602 185 705',
              phoneHref: 'tel:+420602185705',
            },
        ].map((person) => (
          <Box key={person.initials} sx={{
            display: 'flex', alignItems: 'flex-start', gap: 3,
            bgcolor: '#FFFFFF', border: '1px solid #eef0f2',
            borderRadius: '20px', p: 3.5,
            transition: 'box-shadow 0.3s',
            '&:hover': { boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }
          }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: '50%',
              bgcolor: '#E6F1FB', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0,
              fontWeight: 700, fontSize: '1rem', color: '#0C447C',
              fontFamily: '"Inter", sans-serif'
            }}>
              {person.initials}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#000', mb: 0.2, lineHeight: 1.3 }}>
                {person.name}
              </Typography>
              {person.title ? (
                <Typography sx={{ fontSize: '0.78rem', color: '#999', mb: 1.5 }}>
                  {person.title}
                </Typography>
              ) : (
                <Box sx={{ mb: 1.5 }} />
              )}
              <Stack spacing={0.7}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LanguageIcon sx={{ fontSize: '0.95rem', color: '#0066FF', flexShrink: 0 }} />
                  <Typography
                    component="a" href={person.webHref} target="_blank" rel="noopener noreferrer"
                    sx={{ fontSize: '0.85rem', color: '#0066FF', textDecoration: 'none', fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {person.web}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: '0.95rem', color: '#0066FF', flexShrink: 0 }} />
                  <Typography
                    component="a" href={`mailto:${person.email}`}
                    sx={{ fontSize: '0.85rem', color: '#0066FF', textDecoration: 'none', fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {person.email}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: '0.95rem', color: '#666', flexShrink: 0 }} />
                  <Typography
                    component="a" href={person.phoneHref}
                    sx={{ fontSize: '0.85rem', color: '#444', textDecoration: 'none', fontWeight: 600,
                      '&:hover': { color: '#0066FF' } }}
                  >
                    {person.phone}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        ))}
      </Box>
    </FadeInOnScroll>
  </Container>
</Box>

        {/* FOOTER */}
        <Box component="footer" sx={{ bgcolor: '#050505', color: '#fff', pt: 10, pb: 4 }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 6, md: 12 }}>
              <Grid item xs={12} md={6}>
                {/* LOGO AREA */}
                <Box sx={{ mb: 3 }}>
                  <img
                    src="/simple-blue-logo-inverted.png"
                    alt="CRONET"
                    style={{
                      height: '155px',
                      width: 'auto',
                      display: 'block',
                      filter: 'drop-shadow(0 0 10px rgba(0, 102, 255, 0.2))' // Optional: adds a soft bloom to the glow
                    }}
                  />
                </Box>

                <Typography sx={{ color: '#666', fontWeight: 500, fontSize: '0.9rem', mb: 5, maxWidth: '320px', lineHeight: 1.6 }}>
                  {t.heroTitle}
                </Typography>

                <Typography
                  component="a"
                  href="https://cronet.cz"
                  sx={{
                    color: '#0066FF',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontSize: '1rem',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateX(4px)', color: '#3385FF' }
                  }}
                >
                  <Box sx={{ width: 16, height: 2, bgcolor: 'currentColor' }} /> cronet.cz
                </Typography>
              </Grid>

              {/* CONTACT AREA - Added pt to align with logo center */}
              <Grid item xs={12} md={6} sx={{ pt: { md: '25px !important' } }}>
                <Typography variant="overline" sx={{ color: '#0066FF', fontWeight: 800, letterSpacing: 3, display: 'block', mb: 5 }}>
                  {t.contactLabel}
                </Typography>

                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <Typography sx={{ color: '#444', fontWeight: 900, fontSize: '0.75rem', mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                      {t.hqLabel}
                    </Typography>
                    <Typography sx={{ color: '#888', fontSize: '0.9rem', lineHeight: 1.8 }}>
                      CRONET a.s.<br />
                      Slavíčková 827/1<br />
                      638 00 Brno, CZ
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography sx={{ color: '#444', fontWeight: 900, fontSize: '0.75rem', mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                      {t.contactSubLabel}
                    </Typography>
                    <Typography sx={{
                      color: '#888',
                      fontSize: '0.9rem',
                      mb: 1,
                      display: 'block',
                      textDecoration: 'none',
                      '&:hover': { color: '#fff' }
                    }} component="a" href="tel:+420724553246">
                      +420 724 553 246
                    </Typography>
                    <Typography sx={{
                      color: '#888',
                      fontSize: '0.9rem',
                      textDecoration: 'none',
                      '&:hover': { color: '#fff' }
                    }} component="a" href="mailto:info@cronet.cz">
                      info@cronet.cz
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider sx={{ borderColor: '#111', mt: 10, mb: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#333' }}>
                © {new Date().getFullYear()} CRONET a.s.
              </Typography>
            </Box>
          </Container>
        </Box>
    </ThemeProvider>
  );
}

// --- ROOT APP WITH ROUTER ---
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<WebPage />} />
        <Route path="/en" element={<WebPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
}